import React, { useMemo, useState } from 'react';
import { Search, Copy, Check, ExternalLink, Terminal } from 'lucide-react';

// ---- inputs the investigator knows about the subject ----
interface Seed {
  name: string;
  username: string;
  employer: string;
  location: string;
  email: string;
  phone: string;
  domain: string;
  keywords: string;
}

interface Dork {
  category: string;
  label: string;
  query: string;
}

const ENGINES = {
  Google: (q: string) => `https://www.google.com/search?q=${encodeURIComponent(q)}`,
  Bing: (q: string) => `https://www.bing.com/search?q=${encodeURIComponent(q)}`,
  DuckDuckGo: (q: string) => `https://duckduckgo.com/?q=${encodeURIComponent(q)}`,
  Yandex: (q: string) => `https://yandex.com/search/?text=${encodeURIComponent(q)}`,
};
type Engine = keyof typeof ENGINES;

// wrap a value in quotes if it contains a space, so it's treated as an exact phrase
const q = (v: string) => {
  const t = v.trim();
  return /\s/.test(t) ? `"${t}"` : t;
};

const SOCIAL_SITES = [
  'linkedin.com',
  'twitter.com',
  'x.com',
  'facebook.com',
  'instagram.com',
  'tiktok.com',
];

function buildDorks(s: Seed): Dork[] {
  const out: Dork[] = [];
  const name = s.name.trim();
  const nameQ = name ? `"${name}"` : '';
  const add = (category: string, label: string, query: string) =>
    out.push({ category, label, query: query.trim().replace(/\s+/g, ' ') });

  // ---- Identity ----
  if (name) {
    add('Identity', 'Exact name', nameQ);
    if (s.location.trim()) add('Identity', 'Name + location', `${nameQ} ${q(s.location)}`);
    if (s.employer.trim()) add('Identity', 'Name + employer', `${nameQ} ${q(s.employer)}`);
    if (s.location.trim() && s.employer.trim())
      add('Identity', 'Name + employer + location', `${nameQ} ${q(s.employer)} ${q(s.location)}`);
  }

  // ---- Social profiles ----
  const socialClause = SOCIAL_SITES.map((d) => `site:${d}`).join(' OR ');
  if (name) add('Social profiles', 'Name across social sites', `${nameQ} (${socialClause})`);
  if (s.username.trim()) {
    add('Social profiles', 'Handle across social sites', `"${s.username.trim()}" (${socialClause})`);
    add('Social profiles', 'Exact handle', `"${s.username.trim()}"`);
  }
  if (name && s.employer.trim())
    add('Social profiles', 'LinkedIn: name + employer', `site:linkedin.com ${nameQ} ${q(s.employer)}`);

  // ---- Documents ----
  if (name) {
    add('Documents', 'Name in PDFs', `${nameQ} filetype:pdf`);
    add(
      'Documents',
      'Name in office docs',
      `${nameQ} (filetype:pdf OR filetype:doc OR filetype:docx OR filetype:xls OR filetype:xlsx)`
    );
    add('Documents', 'CV / résumé mentions', `${nameQ} (CV OR resume OR "curriculum vitae")`);
  }

  // ---- Contact ----
  if (s.email.trim()) {
    add('Contact', 'Exact email', `"${s.email.trim()}"`);
    if (name) add('Contact', 'Name + email', `${nameQ} "${s.email.trim()}"`);
  }
  if (s.phone.trim()) add('Contact', 'Exact phone', `"${s.phone.trim()}"`);

  // ---- Date of birth / personal ----
  if (name) {
    add('Personal', 'Name + "date of birth"', `${nameQ} ("date of birth" OR "born" OR DOB)`);
    if (s.location.trim())
      add('Personal', 'Name + address terms', `${nameQ} ${q(s.location)} (address OR lives OR resident)`);
  }

  // ---- Domain / web presence ----
  if (s.domain.trim()) {
    const d = s.domain.trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    add('Domain', 'Everything indexed on domain', `site:${d}`);
    add('Domain', 'Documents on domain', `site:${d} (filetype:pdf OR filetype:xlsx OR filetype:docx)`);
    add('Domain', 'Login / admin pages', `site:${d} (inurl:login OR inurl:admin OR intitle:"index of")`);
    if (name) add('Domain', 'Name on domain', `site:${d} ${nameQ}`);
  }

  // ---- Exposure ----
  if (name || s.email.trim()) {
    const who = nameQ || `"${s.email.trim()}"`;
    add('Exposure', 'Possible leaked files', `${who} (intext:password OR intext:confidential)`);
    add(
      'Exposure',
      'Paste sites & drives',
      `${who} (site:pastebin.com OR site:github.com OR site:drive.google.com)`
    );
  }

  // ---- Free-text keywords bolt-on ----
  if (s.keywords.trim() && name) add('Custom', 'Name + your keywords', `${nameQ} ${s.keywords.trim()}`);

  return out;
}

const FIELDS: { key: keyof Seed; label: string; placeholder: string }[] = [
  { key: 'name', label: 'Full name', placeholder: 'Jane Q. Doe' },
  { key: 'username', label: 'Username / handle', placeholder: 'janedoe' },
  { key: 'employer', label: 'Employer / work', placeholder: 'Acme Corp' },
  { key: 'location', label: 'Town / area', placeholder: 'Leeds' },
  { key: 'email', label: 'Email', placeholder: 'jane@example.com' },
  { key: 'phone', label: 'Phone', placeholder: '+44 7700 900000' },
  { key: 'domain', label: 'Website / domain', placeholder: 'example.com' },
  { key: 'keywords', label: 'Extra keywords', placeholder: 'sailing club treasurer' },
];

const DorkBuilder: React.FC = () => {
  const [seed, setSeed] = useState<Seed>({
    name: '',
    username: '',
    employer: '',
    location: '',
    email: '',
    phone: '',
    domain: '',
    keywords: '',
  });
  const [engine, setEngine] = useState<Engine>('Google');
  const [copied, setCopied] = useState<string | null>(null);

  const dorks = useMemo(() => buildDorks(seed), [seed]);
  const categories = useMemo(() => Array.from(new Set(dorks.map((d) => d.category))), [dorks]);
  const anyInput = Object.values(seed).some((v) => v.trim());

  const set = (key: keyof Seed) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setSeed((prev) => ({ ...prev, [key]: e.target.value }));

  const copy = async (query: string) => {
    try {
      await navigator.clipboard.writeText(query);
      setCopied(query);
      setTimeout(() => setCopied((c) => (c === query ? null : c)), 1500);
    } catch {
      /* clipboard blocked — ignore */
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-orange-200 dark:border-orange-500/30 bg-orange-50/60 dark:bg-orange-500/5 p-4">
      <div className="flex items-center gap-2 mb-1">
        <Terminal className="w-4 h-4 text-orange-500" />
        <h4 className="font-bold text-slate-900 dark:text-slate-100">Google dork builder</h4>
      </div>
      <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
        Fill in whatever you know — it generates ready-to-run search queries. Only fields you complete
        are used, so partial info is fine.
      </p>

      {/* inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
              {f.label}
            </label>
            <input
              value={seed[f.key]}
              onChange={set(f.key)}
              placeholder={f.placeholder}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        ))}
      </div>

      {/* engine selector */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-xs font-medium text-gray-500 dark:text-slate-400">Search with:</span>
        <div className="flex gap-1 bg-white dark:bg-slate-800 rounded-lg p-1 border border-gray-200 dark:border-slate-700">
          {(Object.keys(ENGINES) as Engine[]).map((e) => (
            <button
              key={e}
              onClick={() => setEngine(e)}
              className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${
                engine === e
                  ? 'bg-orange-500 text-white'
                  : 'text-gray-600 dark:text-slate-300 hover:bg-orange-50 dark:hover:bg-orange-500/10'
              }`}
            >
              {e}
            </button>
          ))}
        </div>
      </div>

      {/* generated dorks */}
      {!anyInput ? (
        <p className="text-sm text-gray-400 dark:text-slate-500 italic">
          Enter at least one field above to generate dorks.
        </p>
      ) : dorks.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-slate-500 italic">
          Add a name, email, username or domain to generate useful queries.
        </p>
      ) : (
        <div className="space-y-4">
          {categories.map((cat) => (
            <div key={cat}>
              <p className="text-xs font-bold tracking-wide uppercase text-orange-500 mb-1.5">{cat}</p>
              <div className="space-y-1.5">
                {dorks
                  .filter((d) => d.category === cat)
                  .map((d) => (
                    <div
                      key={d.query}
                      className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 px-3 py-2"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-400 dark:text-slate-500">{d.label}</div>
                        <code className="block text-sm text-slate-900 dark:text-slate-100 font-mono break-all">
                          {d.query}
                        </code>
                      </div>
                      <button
                        onClick={() => copy(d.query)}
                        title="Copy query"
                        className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-500/10 transition-colors"
                      >
                        {copied === d.query ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                      <a
                        href={ENGINES[engine](d.query)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Search on ${engine}`}
                        className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                      >
                        <Search className="w-3.5 h-3.5" />
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DorkBuilder;
